import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import { Button } from 'design-system/ui/atoms/button'
import { Check, Pencil, Trash2, X } from 'lucide-react'

interface AdminWebhookProps {
  icon: ReactNode
  title: string
  description: ReactNode
  children?: ReactNode
  webhookUrl: string
  onDelete: () => void
  onSave: (url: string, callbackFn: () => void) => void
  onChange: (url: string) => void
  isEditing: boolean
  setIsEditing: (isEditing: boolean) => void
}

export function AdminWebhookSection({
  icon,
  title,
  description,
  children,
  webhookUrl,
  onDelete,
  onSave,
  onChange,
  isEditing,
  setIsEditing,
}: AdminWebhookProps) {
  const previousUrlRef = useRef(webhookUrl)

  useEffect(() => {
    if (!isEditing) {
      previousUrlRef.current = webhookUrl
    }
  }, [webhookUrl, isEditing])

  const handleSave = () => {
    onSave(webhookUrl, () => {
      previousUrlRef.current = webhookUrl
      setIsEditing(false)
    })
  }

  const handleCancel = () => {
    onChange(previousUrlRef.current)
    setIsEditing(false)
  }

  const handleEdit = () => {
    previousUrlRef.current = webhookUrl
    setIsEditing(true)
  }

  const handleDelete = () => {
    previousUrlRef.current = webhookUrl
    onDelete()
  }

  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700">
      <div className="p-6">
        <div className="flex items-center space-x-2">
          <div className="h-5 w-5 text-gray-900 dark:text-gray-100">{icon}</div>
          <h3 className="text-[14px] font-semibold text-gray-900 dark:text-gray-100">
            {title}
          </h3>
        </div>
        <div className="mt-1.5 text-[12px] text-gray-500 dark:text-gray-400">
          {description}
        </div>
      </div>
      <div className="space-y-6 px-6 pb-6">
        <div className="space-y-2">
          <div className="flex space-x-2">
            <input
              type="text"
              value={webhookUrl}
              onChange={(e) => onChange(e.target.value)}
              placeholder="https://example.com/webhook"
              className="focus:ring-ds-blue-500 focus:border-ds-blue-500 flex-1 rounded-md border border-gray-300 bg-white px-3 py-1 text-[14px] text-[#000000] shadow-sm focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:bg-gray-100 dark:border-gray-700 dark:disabled:bg-gray-800"
              disabled={!isEditing}
            />
            {isEditing ? (
              <>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={!webhookUrl}
                  size="36"
                  uplifted={false}
                >
                  <Check className="mr-1 h-4 w-4" />
                  Save
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleCancel}
                  size="36"
                  uplifted={false}
                >
                  <X className="mr-1 h-4 w-4" />
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  onClick={handleEdit}
                  size="36"
                  uplifted={false}
                >
                  <Pencil className="mr-1 h-4 w-4" />
                  Edit
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={!webhookUrl}
                  size="36"
                  uplifted={false}
                >
                  <Trash2 className="mr-1 h-4 w-4" />
                  Delete
                </Button>
              </>
            )}
          </div>
        </div>
        {children}
      </div>
    </div>
  )
}
